import { createApi } from '@reduxjs/toolkit/query/react';
import {
  SearchFormData,
  SearchFormHeaderData,
  GetCOIResponse,
  CreateFormRequest,
  CreateFormResponse,
  GetFormStatusesResponse,
  GetFormTypesResponse,
  CreateEmailTemplateRequest,
  GetEmailTemplatesResponse,
  GetEmailTemplateByIdResponse,
  GetQualityLeadersResponse,
  GetQualityLeaderApproversResponse,
  UpdateQualityLeaderApproversRequest,
  UpdateQualityLeaderApproversResponse,
  UpdateEmailTemplateRequest,
} from '../../types/forms';
import {
  QLFormData,
  QLSubmitPayload,
} from '../../components/sections/COIQualityLeaderSection/types';
import { baseApiQuery } from '../basequery';
import { PaginationInfo } from '../../services/FormService';
import { entityApi } from './entity.api';

export const formsApi = createApi({
  reducerPath: 'formsApi',
  baseQuery: baseApiQuery,
  tagTypes: ['MyFormsID'],
  endpoints: (builder) => ({
    /**
     * Fetches forms with optional filters, pagination, and search
     * @param params - { data: stringified JSON of filters, pagination, etc. }
     */
    getForms: builder.query<
      { data: { forms: SearchFormHeaderData[]; pagination: PaginationInfo } },
      { params: Record<string, unknown> }
    >({
      query: ({ params }) => ({
        url: `/forms`,
        method: 'GET',
        params,
      }),
      providesTags: () => [{ type: 'MyFormsID', id: 'LIST' }],
    }),

    getForm: builder.query<GetCOIResponse, { id: number }>({
      query: ({ id }) => ({ url: `/forms/${id}`, method: 'GET' }),
      providesTags: (_result, _error, { id }) => [{ type: 'MyFormsID', id }],
    }),

    createForm: builder.mutation<CreateFormResponse, CreateFormRequest>({
      query: (formData) => ({
        url: '/forms',
        method: 'POST',
        body: formData,
      }),
      // invalidatesTags: [{ type: 'MyFormsID', id: 'LIST' }],
    }),

    updateForm: builder.mutation<
      SearchFormHeaderData,
      { id: number; data: Partial<SearchFormData> }
    >({
      query: ({ id, data }) => ({
        url: `/forms/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'MyFormsID', id }],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(entityApi.util.invalidateTags([{ type: 'FormEntities', id }]));
        } catch {
          return;
        }
      },
    }),
    deleteForm: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/forms/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'MyFormsID', id }],
    }),
    getFormStatuses: builder.query<GetFormStatusesResponse, void>({
      query: () => ({
        url: '/forms/statuses',
        method: 'GET',
      }),
    }),
    getFormTypes: builder.query<GetFormTypesResponse, void>({
      query: () => ({
        url: '/forms/types',
        method: 'GET',
      }),
    }),
    submitQlSection: builder.mutation<
      {
        data: {
          FormID: number;
          FormStatus: string;
          emailNotificationSent?: boolean;
        };
      },
      { id: number; data: QLSubmitPayload }
    >({
      query: ({ id, data }) => ({
        url: `/forms/${id}/ql-section`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'MyFormsID', id }],
    }),
    returnQlToPointOfContact: builder.mutation<
      {
        data: {
          FormID: number;
          FormStatus: string;
          emailNotificationSent: boolean;
        };
      },
      { id: number; data: { comments: string } }
    >({
      query: ({ id, data }) => ({
        url: `/forms/${id}/ql-section/return-to-poc`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'MyFormsID', id }],
    }),
    // This endpoint is for generating the PDF of the form.
    generatePdf: builder.mutation<any, { id: number }>({
      query: ({ id }) => ({
        url: `/forms/${id}/generate-pdf`,
        method: 'POST',
      }),
    }),
    getQlSection: builder.query<{ data: QLFormData }, { id: number }>({
      query: ({ id }) => ({ url: `/forms/${id}/ql-section`, method: 'GET' }),
    }),
    createEmailTemplate: builder.mutation<
      { data?: unknown; message?: string },
      CreateEmailTemplateRequest
    >({
      query: (payload) => ({
        url: '/templates/emails',
        method: 'POST',
        body: payload,
      }),
    }),
    updateEmailTemplate: builder.mutation<
      { data?: unknown; message?: string },
      { id: number; payload: UpdateEmailTemplateRequest }
    >({
      query: ({ id, payload }) => ({
        url: `/templates/emails/${id}`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    sendTestEmail: builder.mutation<
      { data?: unknown; message?: string },
      CreateEmailTemplateRequest
    >({
      query: (payload) => ({
        url: '/templates/emails/test',
        method: 'POST',
        body: payload,
      }),
    }),
    getEmailTemplates: builder.query<
      GetEmailTemplatesResponse,
      { params: Record<string, unknown> }
    >({
      query: ({ params }) => ({
        url: '/templates/emails',
        method: 'GET',
        params,
      }),
    }),
    getEmailTemplateById: builder.query<GetEmailTemplateByIdResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/templates/emails/${id}`,
        method: 'GET',
      }),
    }),
    getManageQualityLeaders: builder.query<
      GetQualityLeadersResponse,
      { params: Record<string, unknown> }
    >({
      query: ({ params }) => ({
        url: '/qualityleaders/approvers',
        method: 'GET',
        params,
      }),
    }),
    getQualityLeaderList: builder.query<GetQualityLeaderApproversResponse, string | undefined>({
      query: (searchTerm) => ({
        url: '/qualityleaders',
        method: 'GET',
        params: { q: searchTerm },
      }),
    }),
    updateQualityLeaderApprovers: builder.mutation<
      UpdateQualityLeaderApproversResponse,
      UpdateQualityLeaderApproversRequest
    >({
      query: ({ serviceGroupId, data }) => ({
        url: `/qualityleaders/${serviceGroupId}/approvers`,
        method: 'PUT',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetFormQuery,
  useLazyGetFormQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
  useLazyGetFormsQuery,
  useGetFormStatusesQuery,
  useLazyGetFormStatusesQuery,
  useGetFormTypesQuery,
  useLazyGetFormTypesQuery,
  useSubmitQlSectionMutation,
  useReturnQlToPointOfContactMutation,
  useGeneratePdfMutation,
  useGetQlSectionQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useSendTestEmailMutation,
  useLazyGetEmailTemplatesQuery,
  useGetEmailTemplateByIdQuery,
  useLazyGetManageQualityLeadersQuery,
  useGetQualityLeaderListQuery,
  useLazyGetQualityLeaderListQuery,
  useUpdateQualityLeaderApproversMutation,
} = formsApi;
